import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Pill,
  Plus,
  Search,
  Calendar,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Medications() {
  const { user, organization } = useAuth();
  const [marEntries, setMarEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newMedication, setNewMedication] = useState({
    client_id: '',
    medication_name: '',
    dosage: '',
    frequency: '',
    route: 'oral',
    prescribed_by: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    special_instructions: '',
  });

  useEffect(() => {
    fetchData();
  }, [organization, selectedClient]);

  const fetchData = async () => {
    try {
      const params = { organization_id: organization?.id };
      if (selectedClient !== 'all') params.client_id = selectedClient;
      
      const [marRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/mar`, { params }),
        axios.get(`${API_URL}/api/clients`, { params: { organization_id: organization?.id } })
      ]);
      
      setMarEntries(marRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMedication = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/mar`, {
        ...newMedication,
        organization_id: organization?.id,
      });
      toast.success('Medication added to MAR chart');
      setIsDialogOpen(false);
      setNewMedication({
        client_id: '',
        medication_name: '',
        dosage: '',
        frequency: '',
        route: 'oral',
        prescribed_by: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        special_instructions: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error creating medication:', error);
      toast.error('Failed to add medication');
    }
  };

  const handleAdminister = async (marEntryId) => {
    try {
      await axios.post(`${API_URL}/api/mar/administer?mar_entry_id=${marEntryId}`);
      toast.success('Medication administered and recorded');
      fetchData();
    } catch (error) {
      console.error('Error recording administration:', error);
      toast.error('Failed to record administration');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : 'Unknown';
  };

  const filteredEntries = marEntries.filter(entry => {
    return entry.medication_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           getClientName(entry.client_id).toLowerCase().includes(searchTerm.toLowerCase());
  });

  const frequencyOptions = [
    { value: 'once_daily', label: 'Once Daily' },
    { value: 'twice_daily', label: 'Twice Daily' },
    { value: 'three_times_daily', label: 'Three Times Daily' },
    { value: 'four_times_daily', label: 'Four Times Daily' },
    { value: 'as_needed', label: 'As Needed (PRN)' },
    { value: 'weekly', label: 'Weekly' },
  ];

  const routeOptions = [
    { value: 'oral', label: 'Oral' },
    { value: 'topical', label: 'Topical' },
    { value: 'injection', label: 'Injection' },
    { value: 'inhaled', label: 'Inhaled' },
    { value: 'sublingual', label: 'Sublingual' },
    { value: 'rectal', label: 'Rectal' },
    { value: 'transdermal', label: 'Transdermal Patch' },
  ];

  return (
    <div className="min-h-screen" data-testid="medications-page">
      <Header title="MAR Charts" />
      
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search medications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="medication-search"
              />
            </div>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-48" data-testid="client-filter">
                <SelectValue placeholder="Filter by client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.first_name} {client.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-medication-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Medication
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Medication</DialogTitle>
                <DialogDescription>
                  Add a medication to a client's MAR chart
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateMedication} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="client_id">Client *</Label>
                  <Select 
                    value={newMedication.client_id}
                    onValueChange={(v) => setNewMedication(prev => ({ ...prev, client_id: v }))}
                  >
                    <SelectTrigger data-testid="med-client">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.first_name} {client.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="medication_name">Medication Name *</Label>
                    <Input
                      id="medication_name"
                      value={newMedication.medication_name}
                      onChange={(e) => setNewMedication(prev => ({ ...prev, medication_name: e.target.value }))}
                      placeholder="e.g., Paracetamol"
                      required
                      data-testid="med-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dosage">Dosage *</Label>
                    <Input
                      id="dosage"
                      value={newMedication.dosage}
                      onChange={(e) => setNewMedication(prev => ({ ...prev, dosage: e.target.value }))}
                      placeholder="e.g., 500mg"
                      required
                      data-testid="med-dosage"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="frequency">Frequency *</Label>
                    <Select 
                      value={newMedication.frequency}
                      onValueChange={(v) => setNewMedication(prev => ({ ...prev, frequency: v }))}
                    >
                      <SelectTrigger data-testid="med-frequency">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="route">Route *</Label>
                    <Select 
                      value={newMedication.route}
                      onValueChange={(v) => setNewMedication(prev => ({ ...prev, route: v }))}
                    >
                      <SelectTrigger data-testid="med-route">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {routeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={newMedication.start_date}
                      onChange={(e) => setNewMedication(prev => ({ ...prev, start_date: e.target.value }))}
                      required
                      data-testid="med-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={newMedication.end_date}
                      onChange={(e) => setNewMedication(prev => ({ ...prev, end_date: e.target.value }))}
                      data-testid="med-end-date"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="prescribed_by">Prescribed By</Label>
                  <Input
                    id="prescribed_by"
                    value={newMedication.prescribed_by}
                    onChange={(e) => setNewMedication(prev => ({ ...prev, prescribed_by: e.target.value }))}
                    placeholder="e.g., Dr. Smith"
                    data-testid="med-prescribed-by"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="special_instructions">Special Instructions</Label>
                  <Textarea
                    id="special_instructions"
                    value={newMedication.special_instructions}
                    onChange={(e) => setNewMedication(prev => ({ ...prev, special_instructions: e.target.value }))}
                    rows={3}
                    placeholder="Any special instructions for administration..."
                    data-testid="med-instructions"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-medication">
                    Add Medication
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Medications</p>
                  <p className="text-3xl font-bold text-teal-600">
                    {marEntries.filter(m => m.status === 'active').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Pill className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Clients on Medication</p>
                  <p className="text-3xl font-bold">
                    {new Set(marEntries.map(m => m.client_id)).size}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Due Today</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {marEntries.filter(m => m.status === 'active').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* MAR Table */}
        <Card data-testid="mar-table">
          <CardHeader>
            <CardTitle>MAR Chart</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-slate-100 animate-pulse rounded" />
                ))}
              </div>
            ) : filteredEntries.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Medication</TableHead>
                    <TableHead>Dosage</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`mar-row-${entry.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-teal-600" />
                          </div>
                          <span className="font-medium">{getClientName(entry.client_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{entry.medication_name}</TableCell>
                      <TableCell>{entry.dosage}</TableCell>
                      <TableCell className="capitalize">{entry.frequency?.replace('_', ' ')}</TableCell>
                      <TableCell className="capitalize">{entry.route}</TableCell>
                      <TableCell>{entry.start_date}</TableCell>
                      <TableCell>
                        <Badge variant={entry.status === 'active' ? 'default' : 'secondary'}>
                          {entry.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => handleAdminister(entry.id)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`administer-${entry.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Given
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Pill className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No medications found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Add First Medication
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
